/**
 * Date utility functions with consistent IST timezone handling
 */

const IST_OFFSET = 5.5 * 60 * 60 * 1000; // IST offset in milliseconds (UTC+5:30)

/**
 * Returns today's date in YYYY-MM-DD format in IST timezone
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getTodayDate() {
    const now = new Date();
    const istTime = new Date(now.getTime() + IST_OFFSET);
    return istTime.toISOString().split('T')[0];
}

/**
 * Format a date for comparison in IST timezone (strips time component)
 * @param {string|Date} date - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export function formatDateForComparison(date) {
    const d = new Date(date);
    const istTime = new Date(d.getTime() + IST_OFFSET);
    return istTime.toISOString().split('T')[0];
}

/**
 * Check if two dates are the same day in IST timezone
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @returns {boolean} True if dates are the same day
 */
export function isSameDay(date1, date2) {
    return formatDateForComparison(date1) === formatDateForComparison(date2);
}

/**
 * Format a date for display in IST timezone
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDateForDisplay(date) {
    const d = new Date(date);
    const istTime = new Date(d.getTime() + IST_OFFSET);
    return istTime.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
}

/**
 * Get relative date description (e.g., "Today", "Yesterday", etc.) in IST timezone
 * @param {string|Date} date - Date to describe
 * @returns {string} Relative date description
 */
export function getRelativeDateDescription(date) {
    const today = getTodayDate();
    const compareDate = formatDateForComparison(date);
    
    if (compareDate === today) {
        return 'Today';
    }
    
    // Calculate yesterday's date in IST timezone more reliably
    const yesterday = getPreviousDate(today);
    if (compareDate === yesterday) {
        return 'Yesterday';
    }
    
    return formatDateForDisplay(date);
}

/**
 * Check if a date is in the past in IST timezone
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export function isDateInPast(date) {
    const today = getTodayDate();
    const compareDate = formatDateForComparison(date);
    return compareDate < today;
}

/**
 * Check if a date is today or in the past (uses <= comparison)
 * Useful for "overdue" checks - a date is considered passed when it's today or before today
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is today or in the past
 */
export function isDateOnOrBeforeToday(date) {
    if (!date) return false;
    const today = getTodayDate();
    const compareDate = formatDateForComparison(date);
    return compareDate <= today;
}

/**
 * Check if a work plan needs attention (not closed and in the past)
 * @param {Object} workPlan - Work plan object
 * @returns {boolean} True if work plan needs attention
 */
export function doesWorkPlanNeedAttention(workPlan) {
    if (!workPlan) return false;
    return !workPlan.is_closed && isDateInPast(workPlan.date);
} 

/**
 * Get today's date in local timezone (YYYY-MM-DD format)
 * This is useful for date inputs and UI components that should reflect the user's local date
 * @returns {string} Today's date in YYYY-MM-DD format (local timezone)
 */
export function getTodayLocalDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
} 

/**
 * Format hours for display to avoid floating-point precision issues (e.g. 10.100000000000001 -> 10.10)
 * @param {number|string} hours - Hours value to format
 * @returns {number} Hours rounded to 2 decimal places
 */
export function formatHours(hours) {
    const num = parseFloat(hours);
    if (isNaN(num)) return 0;
    return parseFloat(num.toFixed(2));
}

/**
 * Get previous day's date string
 * @param {string} date - Date string in YYYY-MM-DD format
 * @returns {string} Previous day's date in YYYY-MM-DD format
 */
export function getPreviousDate(date) {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
} 